import { type FormEvent, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageDetailHeader } from "../../components/PageDetailHeader";
import { Container } from "../../components/Container";
import { Card } from "../../components/Card";
import { Button, RouterButtonLink } from "../../components/Button";
import { LoadingContainer } from "../../components/LoadingContainer";
import { InfoBox } from "../../components/InfoBox";
import { Input } from "../../components/Input";
import { Form } from "../../components/Form";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { FormActions } from "../../components/FormActions";
import {
  useCreateStoreDebtSettlementMutation,
  useDownloadStoreDebtReportPdfMutation,
  useGetStoreDebtQuery,
} from "../../query/storeDebtsQuery";
import type { ChainId, StoreDebtId } from "@KassaroAB/shared/types/ids";
import { formatMoney } from "../../utils/formatMoney";
import { formatMoneyAmountInputValue } from "../../utils/parseMoneyAmountInput";
import { getStoreDebtOwed, pairTitle } from "./storeDebtDisplay";

export const StoreDebtSettleView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { chainId, storeDebtId } = useParams<{
    chainId: ChainId;
    storeDebtId: StoreDebtId;
  }>();
  const [amountText, setAmountText] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [reference, setReference] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const {
    data: storeDebt,
    isLoading,
    error,
    refetch,
  } = useGetStoreDebtQuery(chainId, storeDebtId ?? null);
  const { mutateAsync: createSettlement, isPending } =
    useCreateStoreDebtSettlementMutation(chainId);
  const { mutateAsync: downloadReport, isPending: isDownloadingPdf } =
    useDownloadStoreDebtReportPdfMutation(chainId);

  const owed = storeDebt ? getStoreDebtOwed(storeDebt) : null;
  const detailPath = `/chains/${chainId}/storeDebts/${storeDebtId}`;
  const title = pairTitle(storeDebt, t("storeDebtsView.detailTitle"));

  const handleSettle = (event: FormEvent) => {
    event.preventDefault();
    if (!storeDebt || !owed) {
      return;
    }
    const amountInput =
      amountText ?? formatMoneyAmountInputValue(owed.outstanding);
    const parsed = Number(amountInput.replace(",", "."));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setFormError(t("storeDebtsView.invalidAmount"));
      return;
    }
    const amount = Math.round(parsed * 100);
    if (amount > owed.outstanding) {
      setFormError(t("storeDebtsView.amountExceeds"));
      return;
    }
    setFormError(null);
    createSettlement({
      storeDebtId: storeDebt.id,
      body: {
        amount: {
          amount,
          currency: storeDebt.balanceForStore1.currency,
        },
        note: note.trim() || undefined,
        reference: reference.trim() || undefined,
      },
    })
      .then(() => {
        navigate(detailPath);
      })
      .catch((settleError: unknown) => {
        setFormError(
          settleError instanceof Error
            ? settleError.message
            : t("storeDebtsView.settleFailed"),
        );
      });
  };

  const handleDownloadReport = () => {
    if (!storeDebt) {
      return;
    }
    setFormError(null);
    downloadReport({ storeDebtId: storeDebt.id }).catch(() => {
      setFormError(t("storeDebtsView.downloadReportFailed"));
    });
  };

  return (
    <Container>
      <PageDetailHeader
        title={t("storeDebtsView.settleTitle")}
        parentLabel={title}
        parentTo={detailPath}
        currentLabel={t("storeDebtsView.settleTitle")}
      />
      {isLoading && !storeDebt ? (
        <Card>
          <LoadingContainer message={t("storeDebtsView.loading")} />
        </Card>
      ) : error || !storeDebt || !owed ? (
        <Card>
          <InfoBox variant="error">{t("storeDebtsView.notFound")}</InfoBox>
          <FormActions>
            <Button type="button" $color="secondary" onClick={() => refetch()}>
              {t("common.retry")}
            </Button>
          </FormActions>
        </Card>
      ) : owed.outstanding === 0 ? (
        <Card>
          <InfoBox variant="info">{t("storeDebtsView.nothingToSettle")}</InfoBox>
          <FormActions>
            <RouterButtonLink to={detailPath} $color="secondary">
              {t("common.back")}
            </RouterButtonLink>
          </FormActions>
        </Card>
      ) : (
        <Card>
          <Form onSubmit={handleSettle}>
            <p>
              {t("storeDebtsView.owes", {
                debtor: owed.debtorName,
                creditor: owed.creditorName,
                amount: formatMoney({
                  amount: owed.outstanding,
                  currency: storeDebt.balanceForStore1.currency,
                }),
              })}
            </p>
            <FormGroup>
              <FormLabel htmlFor="store-debt-amount">
                {t("storeDebtsView.amount")}
              </FormLabel>
              <Input
                id="store-debt-amount"
                value={
                  amountText ?? formatMoneyAmountInputValue(owed.outstanding)
                }
                onChange={(changeEvent) =>
                  setAmountText(changeEvent.target.value)
                }
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="store-debt-note">
                {t("storeDebtsView.note")}
              </FormLabel>
              <Input
                id="store-debt-note"
                value={note}
                onChange={(changeEvent) => setNote(changeEvent.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="store-debt-reference">
                {t("storeDebtsView.reference")}
              </FormLabel>
              <Input
                id="store-debt-reference"
                value={reference}
                onChange={(changeEvent) =>
                  setReference(changeEvent.target.value)
                }
              />
            </FormGroup>
            {formError && <InfoBox variant="error">{formError}</InfoBox>}
            <FormActions>
              <RouterButtonLink to={detailPath} $color="secondary">
                {t("common.cancel")}
              </RouterButtonLink>
              <Button
                type="button"
                $color="secondary"
                disabled={isDownloadingPdf}
                onClick={handleDownloadReport}
              >
                {t("storeDebtsView.downloadReport")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {t("storeDebtsView.markAsPaid")}
              </Button>
            </FormActions>
          </Form>
        </Card>
      )}
    </Container>
  );
};
